import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Currency from "components/format/currency/currency";
import PrettyDate from "components/format/PrettyDate";
import StatusChip, { ChipTone } from "components/tw/status-chip";
import { QueueCell, QueueRow, QueueTable } from "features/cabinet-queues/queue-table";
import { BusinessTrip } from "types/BusinessTrip";
import { getBusinessTripStatusTone } from "utils/businessTripStatus";
import { TripNextStepButton, TripNextStepText } from "./trip-next-steps";

// The colour of a status chip for each tone `getBusinessTripStatusTone` gives (none means a draft).
const CHIP_TONES = { success: "green", critical: "red", attention: "amber" } as const;

const statusChipTone = (status: string): ChipTone => {
  const tone = getBusinessTripStatusTone(status);
  return tone ? CHIP_TONES[tone] : "grey";
};

const substituteName = (signatureUser: BusinessTrip["signatureUser"]) =>
  (typeof signatureUser === "object" ? signatureUser?.name : signatureUser) || "-";

interface TripTableProps {
  trips: BusinessTrip[];
  /** The address of the page of a trip. */
  tripPath: (trip: BusinessTrip) => string;
}

/**
 * The person's trips as cards on a phone and a table from the md breakpoint: dates, place,
 * status and next step. The advance and the substitute are columns of the table only.
 */
export default function TripTable({ trips, tripPath }: TripTableProps) {
  const { t } = useTranslation();
  const columns = [
    { heading: t("date"), width: "12rem" },
    { heading: t("place_of_business_trip"), width: "minmax(0,1fr)" },
    { heading: t("status"), width: "8rem" },
    { heading: t("advance_for_expenses"), width: "7rem" },
    { heading: t("substitute_person"), width: "minmax(0,0.8fr)" },
    { heading: t("cabinet_trip_next_step"), width: "minmax(0,1.3fr)" },
  ];

  return (
    <QueueTable label={t("business_trips")} columns={columns}>
      {trips.map((trip) => (
        <TripLine key={trip.id} trip={trip} path={tripPath(trip)} />
      ))}
    </QueueTable>
  );
}

function TripLine({ trip, path }: { trip: BusinessTrip; path: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const status = trip.status || "draft";

  // The dates are a real link that is stretched over the whole card (its ::after covers the
  // wrapper), so a tap anywhere opens the trip, also when the next step has no button. The
  // button of the next step sits above the stretched link. The wrapper has no role of its
  // own, so the row stays a direct child of the row group for a screen reader.
  return (
    <div role="presentation" className="relative">
      <QueueRow>
        <QueueCell isTitle>
          <Link to={path} className="whitespace-nowrap after:absolute after:inset-0 hover:underline">
            <PrettyDate dateString={trip.dateFrom} /> – <PrettyDate dateString={trip.dateTo} />
          </Link>
        </QueueCell>
        <QueueCell>
          {trip.placeOfBusinessTrip || "-"}
          {trip.purpose && <span className="block text-gray-500 line-clamp-2 md:line-clamp-1">{trip.purpose}</span>}
        </QueueCell>
        <QueueCell>
          <StatusChip tone={statusChipTone(status)}>{t(`status_${status}`)}</StatusChip>
        </QueueCell>
        <QueueCell className="hidden md:block md:text-right">
          <Currency value={trip.advanceMoney} isSymbol isDecimal />
        </QueueCell>
        <QueueCell className="hidden md:block">{substituteName(trip.signatureUser)}</QueueCell>
        <QueueCell className="flex w-full flex-wrap items-center justify-between gap-2 md:w-auto md:justify-start">
          <TripNextStepText step={trip.nextStep} />
          <TripNextStepButton step={trip.nextStep} onOpen={() => navigate(path)} />
        </QueueCell>
      </QueueRow>
    </div>
  );
}
